/** Desktop / tablet-landscape shell: persistent rail + content margin. */
export const SHELL_SIDEBAR_DESKTOP_MQ = "(min-width: 1024px)";

/**
 * Ultra-narrow phones: a 48px rail costs ~14% of the viewport, so the shell is
 * hidden entirely and the header toggle opens it as an overlay instead.
 * Keep in sync with the `max-[349px]:` utilities below.
 */
export const SHELL_SIDEBAR_HIDDEN_MQ = "(max-width: 349px)";

export function isShellSidebarDesktopViewport() {
  if (typeof window === "undefined") return false;
  return window.matchMedia(SHELL_SIDEBAR_DESKTOP_MQ).matches;
}

export function isShellSidebarHiddenViewport() {
  if (typeof window === "undefined") return false;
  return window.matchMedia(SHELL_SIDEBAR_HIDDEN_MQ).matches;
}

/** Icon-only rail (labels hidden): menu closed or manually collapsed. */
export function isSidebarCompactNav(isMobileSidebarOpen, isCollapsed) {
  return !isMobileSidebarOpen || isCollapsed;
}

/**
 * Compact-rail flyout chose a child route — next ShellSidebarRouteSync pass
 * must keep the shell collapsed instead of auto-expanding.
 */
let keepShellCompactAfterNav = false;

export function markKeepShellCompactAfterNav() {
  keepShellCompactAfterNav = true;
}

export function consumeKeepShellCompactAfterNav() {
  const keep = keepShellCompactAfterNav;
  keepShellCompactAfterNav = false;
  return keep;
}

/** Below 350px the rail takes no space, so content starts at the viewport edge. */
const MAIN_CONTENT_HIDDEN_RAIL = "max-[349px]:ml-0";

/** Below 350px the collapsed shell is fully hidden — `invisible` also drops its
 *  links out of the tab order, which `w-0` + `overflow-hidden` alone would not. */
const SHELL_HIDDEN_RAIL =
  "max-[349px]:w-0 max-[349px]:invisible max-[349px]:border-r-0";

/** Main content left margin for the shell sidebar.
 *  Mobile: always rail (expand overlays). Desktop: rail or open. */
export function shellSidebarMainContentMarginClass(
  isMobileSidebarOpen,
  isCollapsed,
  isHydrated = true,
) {
  if (!isHydrated) return `ml-12 lg:ml-64 ${MAIN_CONTENT_HIDDEN_RAIL}`;
  return isSidebarCompactNav(isMobileSidebarOpen, isCollapsed)
    ? `ml-12 lg:ml-16 ${MAIN_CONTENT_HIDDEN_RAIL}`
    : `ml-12 lg:ml-64 ${MAIN_CONTENT_HIDDEN_RAIL}`;
}

/** Sidebar shell width — must match `shellSidebarMainContentMarginClass` before hydrate.
 *  Pre-hydrate mobile must stay the icon rail (w-12); a w-64 SSR paints over the
 *  hero and steals / delays LCP on marketing pages. */
export function shellSidebarWidthClass(
  isMobileSidebarOpen,
  isCollapsed,
  isHydrated = true,
) {
  if (!isHydrated) return `w-12 lg:w-64 ${SHELL_HIDDEN_RAIL}`;
  return isSidebarCompactNav(isMobileSidebarOpen, isCollapsed)
    ? `w-12 lg:w-16 ${SHELL_HIDDEN_RAIL}`
    : "w-64";
}

export const shellSidebarTransitionClass =
  "transition-[margin] duration-300 ease-in-out";

/** Collapsed icon-rail width in px on desktop (matches Tailwind lg:w-16). */
export const SHELL_SIDEBAR_RAIL_PX = 64;
/** Narrower icon-rail on mobile (matches Tailwind w-12). */
export const SHELL_SIDEBAR_RAIL_MOBILE_PX = 48;
/** Expanded sidebar width in px (matches Tailwind w-64). */
export const SHELL_SIDEBAR_OPEN_PX = 256;

export function getShellSidebarRailPx() {
  if (typeof window === "undefined") return SHELL_SIDEBAR_RAIL_PX;
  if (window.matchMedia(SHELL_SIDEBAR_DESKTOP_MQ).matches) {
    return SHELL_SIDEBAR_RAIL_PX;
  }
  return isShellSidebarHiddenViewport() ? 0 : SHELL_SIDEBAR_RAIL_MOBILE_PX;
}
